import { isValidElement, useCallback, useRef, useState } from 'react'

const DEFAULT_GAP_PX = 4 // matches PillContainer's gap
const DEFAULT_CHIP_RESERVE_PX = 52 // room reserved for the "+N" overflow chip

/**
 * Renders as many pills as fit on a single line within the live container width,
 * then a caller-supplied "+N" overflow chip for the remainder — recomputing as
 * the column is resized.
 *
 * Measurement vs. display are split into two layers so per-pill truncation is
 * preserved:
 *  - a hidden layer renders every pill at its NATURAL width purely for measuring
 *    (so the fit math sees true widths, not shrunk/truncated ones);
 *  - the visible layer renders the fitted pills bounded by the column width, so
 *    an over-long pill ellipsizes (…) and shows its own tooltip exactly as it
 *    did before, and the "+N" chip is pinned (shrink-0) so it never wraps.
 *
 * Pass a stable `key` from the parent (derived from the item ids) so the
 * measurement cache resets when the underlying items change.
 */
export function FittedPillRow({
  pills,
  renderOverflow,
  gap = DEFAULT_GAP_PX,
  chipReserve = DEFAULT_CHIP_RESERVE_PX,
}: {
  /** One rendered pill element per item, in display order. */
  pills: React.ReactNode[]
  /** Render the overflow chip given the hidden pills and their count. */
  renderOverflow: (hidden: React.ReactNode[], count: number) => React.ReactNode
  gap?: number
  chipReserve?: number
}) {
  const [visibleCount, setVisibleCount] = useState(pills.length)
  const widthsRef = useRef<number[] | null>(null)
  const containerRef = useRef<HTMLElement | null>(null)
  const observerRef = useRef<ResizeObserver | null>(null)

  const compute = useCallback(() => {
    const container = containerRef.current
    const widths = widthsRef.current
    if (!container || !widths) return
    const available = container.clientWidth
    if (!available) return

    let used = 0
    let count = 0
    for (let i = 0; i < widths.length; i++) {
      // A pill never needs more room than the column itself — it truncates.
      const pillWidth = Math.min(widths[i], available)
      const withPill = used + (i > 0 ? gap : 0) + pillWidth
      const isLast = i === widths.length - 1
      // Every pill but the last must also leave room for the "+N" chip.
      const limit = isLast ? available : available - gap - chipReserve
      if (withPill <= limit) {
        used = withPill
        count = i + 1
      } else {
        break
      }
    }
    // Always show at least one pill, even in a very narrow column.
    if (count < 1) count = 1

    setVisibleCount((prev) => (prev === count ? prev : count))
  }, [gap, chipReserve])

  const setMeasureLayer = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return
      const els = node.querySelectorAll<HTMLElement>('[data-measure-pill]')
      widthsRef.current = Array.from(els).map((el) => el.offsetWidth)
      compute()
    },
    [compute]
  )

  const setContainer = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect()
      observerRef.current = null
      containerRef.current = node
      if (!node) return
      if (typeof ResizeObserver !== 'undefined') {
        const observer = new ResizeObserver(() => compute())
        observer.observe(node)
        observerRef.current = observer
      }
      compute()
    },
    [compute]
  )

  const showAll = visibleCount >= pills.length
  const visible = showAll ? pills : pills.slice(0, visibleCount)
  const hidden = showAll ? [] : pills.slice(visibleCount)

  // Signature of the current pill set, derived from each pill's React key
  // (call sites give content-stable keys — e.g. "zip", `value-${key}`). Used
  // to key the measurement layer below: a callback ref only re-fires when its
  // element is replaced, so without this a pills change into an already-mounted
  // cell would leave widthsRef stale and the overflow count wrong. Re-keying
  // the measure layer forces setMeasureLayer to re-run and re-measure — no
  // useEffect, and only the hidden layer remounts (not the whole cell).
  const measureKey = pills
    .map((pill, i) => (isValidElement(pill) ? (pill.key ?? i) : i))
    .join('|')

  return (
    <div className="relative w-full min-w-0">
      {/* Hidden measurement layer: every pill at its natural width. */}
      <div
        key={measureKey}
        ref={setMeasureLayer}
        aria-hidden
        className="pointer-events-none invisible absolute top-0 flex flex-nowrap items-center"
        style={{ left: -9999, gap }}
      >
        {pills.map((pill, i) => (
          <span key={i} data-measure-pill className="inline-flex shrink-0">
            {pill}
          </span>
        ))}
      </div>

      {/* Visible layer: fitted pills (bounded so they truncate) + pinned chip. */}
      <div
        ref={setContainer}
        className="flex w-full min-w-0 flex-nowrap items-center overflow-hidden"
        style={{ gap }}
      >
        {visible.map((pill, i) => (
          <span key={i} className="inline-flex min-w-0 max-w-full">
            {pill}
          </span>
        ))}
        {hidden.length > 0 && (
          <span className="inline-flex shrink-0 whitespace-nowrap">
            {renderOverflow(hidden, hidden.length)}
          </span>
        )}
      </div>
    </div>
  )
}
